import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PayPalButtons } from '@paypal/react-paypal-js';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import AddressAutocompleteField from '../components/checkout/AddressAutocompleteField';
import { useCartStore } from '../store/cartStore';
import { useAuthStore } from '../store/authStore';
import { cartApi, couponApi, orderApi, pointApi, stripeApi } from '../api';
import { UserCoupon, Cart, CartItem } from '../types';
import { toImageUrl } from '../utils/image';
import { formatPrice } from '../utils/price';
import StripePaymentForm from '../components/payment/StripePaymentForm';

const MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

interface PaymentOption {
  value: string;
  label: string;
  sublabel: string;
  badge?: 'recommended' | null;
  disabled?: boolean;
  disabledReason?: string;
}

const PaymentMethodPicker: React.FC<{
  total: number;
  value: string;
  onChange: (v: string) => void;
  stripeEnabled: boolean;
}> = ({ total, value, onChange, stripeEnabled }) => {
  const options: PaymentOption[] = [];

  if (stripeEnabled) {
    options.push({
      value: 'STRIPE',
      label: 'Credit or Debit Card',
      sublabel: 'Visa, Mastercard, Amex — secure card payment',
      badge: total >= 50 ? 'recommended' : null,
    });
  }

  // PayPal: $200 이상이면 Stripe 사용 가능할 때만 차단
  const blockPayPal = total >= 200 && stripeEnabled;
  if (blockPayPal) {
    options.push({
      value: 'PAYPAL',
      label: 'PayPal',
      sublabel: 'Not available for orders over $200',
      disabled: true,
    });
  } else {
    options.push({
      value: 'PAYPAL',
      label: 'PayPal',
      sublabel: total >= 50
        ? 'Buyer protection included'
        : 'Pay with your PayPal balance or card',
    });
  }

  // If currently selected is disabled, fall back to first enabled
  React.useEffect(() => {
    const current = options.find(o => o.value === value);
    if (!current || current.disabled) {
      const first = options.find(o => !o.disabled);
      if (first) onChange(first.value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [total]);

  return (
    <div>
      {total >= 200 && stripeEnabled && (
        <div style={pmStyles.banner}>
          🔒 Orders over $200 — Card payment only for added security.
        </div>
      )}
      <div style={pmStyles.list}>
        {options.map(opt => {
          const selected = opt.value === value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => !opt.disabled && onChange(opt.value)}
              disabled={opt.disabled}
              style={{
                ...pmStyles.card,
                ...(selected && !opt.disabled ? pmStyles.cardSelected : {}),
                ...(opt.disabled ? pmStyles.cardDisabled : {}),
              }}
            >
              <div style={pmStyles.radio}>
                <div style={{ ...pmStyles.radioInner, ...(selected && !opt.disabled ? pmStyles.radioInnerOn : {}) }} />
              </div>
              <div style={{ flex: 1, textAlign: 'left' }}>
                <div style={pmStyles.labelRow}>
                  <span style={pmStyles.label}>{opt.label}</span>
                  {opt.badge === 'recommended' && (
                    <span style={pmStyles.badge}>RECOMMENDED</span>
                  )}
                </div>
                <p style={pmStyles.sublabel}>{opt.sublabel}</p>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

const pmStyles: Record<string, React.CSSProperties> = {
  banner: { padding: '10px 14px', background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 8, fontSize: 12, color: '#795548', marginBottom: 12 },
  list: { display: 'flex', flexDirection: 'column', gap: 10 },
  card: { display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', background: '#fff', border: '1.5px solid #e8e8e8', borderRadius: 10, cursor: 'pointer', textAlign: 'left' },
  cardSelected: { borderColor: '#b39ddb', background: '#fff8f8' },
  cardDisabled: { opacity: 0.5, cursor: 'not-allowed', background: '#fafafa' },
  radio: { width: 18, height: 18, borderRadius: '50%', border: '1.5px solid #ccc', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  radioInner: { width: 10, height: 10, borderRadius: '50%', background: 'transparent' },
  radioInnerOn: { background: '#b39ddb' },
  labelRow: { display: 'flex', alignItems: 'center', gap: 8 },
  label: { fontSize: 14, fontWeight: 600, color: '#333' },
  badge: { fontSize: 9, fontWeight: 700, color: '#fff', background: '#4caf50', padding: '2px 6px', borderRadius: 4, letterSpacing: 0.5 },
  sublabel: { fontSize: 12, color: '#888', marginTop: 2 },
};

// 사업자등록 전까지 Stripe(카드) 결제 숨김 → PayPal만 노출. 등록 후 true로 되돌리면 재표시.
const STRIPE_ENABLED = false;

const CheckoutPage: React.FC = () => {
  const { t } = useTranslation();
  const { cart, fetchCart } = useCartStore();
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const gbState = location.state as { groupBuyId?: number; variantId?: number | null; quantity?: number; groupBuy?: any; selectedKeys?: string[] } | null;
  const [form, setForm] = useState({
    receiverName: user?.name || '',
    receiverPhone: user?.phone || '',
    zipCode: user?.zipCode || '',
    address: user?.address || '',
    addressDetail: user?.addressDetail || '',
    city: '',
    state: '',
    memo: '',
    paymentMethod: 'CARD',
  });
  const [loading, setLoading] = useState(false);
  const [myCoupons, setMyCoupons] = useState<UserCoupon[]>([]);
  const [selectedCoupon, setSelectedCoupon] = useState<UserCoupon | null>(null);
  const [couponOpen, setCouponOpen] = useState(false);
  const [createdOrderId, setCreatedOrderId] = useState<number | null>(null);

  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);
  const [stripeClientSecret, setStripeClientSecret] = useState<string | null>(null);
  const [stripeAmountUsd, setStripeAmountUsd] = useState(0);
  const [stripeIntentId, setStripeIntentId] = useState<string | null>(null);
  const [stripeError, setStripeError] = useState<string | null>(null);

  const [pointsBalance, setPointsBalance] = useState(0);
  const [pointsBalanceUsd, setPointsBalanceUsd] = useState(0);
  const [usePoints, setUsePoints] = useState(false);
  const [combineShipping, setCombineShipping] = useState(true);
  const [quoted, setQuoted] = useState<Cart | null>(null);

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    if (isCart) fetchCart();
    couponApi.getMyCoupons().then(r => setMyCoupons(r.data.data.filter(c => !c.used)));
    pointApi.getMyPoints().then(r => {
      setPointsBalance(r.data.data.balance);
      setPointsBalanceUsd(r.data.data.balanceUsd);
    }).catch(() => {});
  }, []);

  const f = (field: keyof typeof form) => ({
    value: form[field],
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setForm({ ...form, [field]: e.target.value }),
  });

  const handlePlaceSelected = ({ address, city, state, zipCode }: { address: string; city: string; state: string; zipCode: string }) => {
    setForm(prev => ({ ...prev, address, city, state, zipCode }));
  };

  const gbQty = gbState?.quantity ?? 1;
  const gbVariantId = gbState?.variantId ?? null;
  const gbVariantName = gbState?.groupBuy?.variants?.find((v: any) => v.id === gbVariantId)?.name ?? null;
  const gbPrice = gbState?.groupBuy?.priceUsd ?? 0;
  const gbSubtotal = Number(gbPrice) * gbQty;
  const gbShipping = Math.max(19, Math.round((gbState?.groupBuy?.weightGrams ?? 100) * gbQty / 1000 * 15));

  const isCart = !gbState?.groupBuyId;
  const lineKey = (i: CartItem) => `${i.kind}:${i.productId ?? i.groupBuyId}:${i.variantId ?? 0}`;
  const selectedKeys = gbState?.selectedKeys ? new Set(gbState.selectedKeys) : null;
  // 장바구니에서 선택한(체크한) 항목만. 선택정보 없으면 구매가능 전체.
  const activeItems = (cart?.items ?? []).filter(i => i.available && (selectedKeys ? selectedKeys.has(lineKey(i)) : true));

  const cartItemsPayload = () => activeItems
    .filter(i => i.kind === 'PRODUCT')
    .map(i => ({ productId: i.productId, variantId: i.variantId ?? null, quantity: i.quantity }));
  const cartGroupBuyItemsPayload = () => activeItems
    .filter(i => i.kind === 'GROUP_BUY')
    .map(i => ({ groupBuyId: i.groupBuyId, variantId: i.variantId ?? null, quantity: i.quantity }));

  // 선택 항목만의 합계·배송비를 서버에서 견적
  useEffect(() => {
    if (!isCart || !cart) return;
    cartApi.quote(cartItemsPayload(), cartGroupBuyItemsPayload())
      .then(r => setQuoted(r.data.data))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart]);

  const showCombineChoice = isCart && !!quoted?.hasProduct && !!quoted?.hasGroupBuy;
  const subtotalBase = isCart ? Number(quoted?.subtotal ?? 0) : gbSubtotal;
  const shippingBase = isCart
    ? Number((combineShipping ? quoted?.combinedShippingFee : quoted?.separateShippingFee) ?? quoted?.shippingFee ?? 0)
    : gbShipping;

  const getCouponDiscount = () => {
    if (!selectedCoupon) return 0;
    const base = subtotalBase + shippingBase;
    const c = selectedCoupon;
    if (c.minOrderAmount && base < c.minOrderAmount) return 0;
    let d = c.discountType === 'FIXED' ? c.discountValue : base * c.discountValue / 100;
    if (c.maxDiscountAmount) d = Math.min(d, c.maxDiscountAmount);
    return Math.min(d, base);
  };

  const validateDelivery = () => {
    if (!form.receiverName || !form.receiverPhone || !form.zipCode || !form.address) {
      alert(t('order.fillRequired'));
      return false;
    }
    return true;
  };

  const buildShippingAddress = () => ({
    recipientName: form.receiverName,
    country: 'US',
    postalCode: form.zipCode,
    line1: form.address,
    line2: form.addressDetail || undefined,
    city: form.city || form.address || 'N/A',
    state: form.state || '',
    phone: form.receiverPhone,
  });

  // CARD / BANK_TRANSFER → MOCK (서버가 지원하는 providerType으로 매핑)
  const toProviderType = (method: string) => {
    if (method === 'PAYPAL') return 'PAYPAL';
    if (method === 'STRIPE') return 'STRIPE';
    return 'MOCK';
  };

  const handleOrder = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isCart && !gbState?.groupBuyId) return;
    setLoading(true);
    try {
      const res = isCart
        ? await orderApi.createCart({
            items: cartItemsPayload(),
            groupBuyItems: cartGroupBuyItemsPayload(),
            combineShipping,
            shippingAddress: buildShippingAddress(),
            paymentProvider: toProviderType(form.paymentMethod),
            userCouponId: selectedCoupon?.id,
            usedPoints: usedPointsInt,
          })
        : await orderApi.create({
            groupBuyId: gbState?.groupBuyId,
            variantId: gbVariantId,
            quantity: gbQty,
            shippingAddress: buildShippingAddress(),
            paymentProvider: toProviderType(form.paymentMethod),
            userCouponId: selectedCoupon?.id,
            usedPoints: usedPointsInt,
          });
      if (isCart) { await cartApi.clear().catch(() => {}); fetchCart(); }
      alert(t('order.success'));
      navigate(`/orders/${res.data.data.order.id}`);
    } catch (err: any) {
      alert(err.response?.data?.message || t('order.failed'));
    } finally {
      setLoading(false);
    }
  };

  const handlePayPalCreate = async () => {
    const res = isCart
      ? await orderApi.createCart({
          items: cartItemsPayload(),
          groupBuyItems: cartGroupBuyItemsPayload(),
          combineShipping,
          shippingAddress: buildShippingAddress(),
          paymentProvider: 'PAYPAL',
          userCouponId: selectedCoupon?.id,
          usedPoints: usedPointsInt,
        })
      : await orderApi.create({
          groupBuyId: gbState?.groupBuyId,
          variantId: gbVariantId,
          quantity: gbQty,
          shippingAddress: buildShippingAddress(),
          paymentProvider: 'PAYPAL',
          userCouponId: selectedCoupon?.id,
          usedPoints: usedPointsInt,
        });
    setCreatedOrderId(res.data.data.order.id);
    return res.data.data.payment.providerOrderId as string;
  };

  const handlePayPalApprove = async () => {
    if (!createdOrderId) return;
    setLoading(true);
    try {
      await orderApi.capture(createdOrderId);
      if (isCart) { await cartApi.clear().catch(() => {}); fetchCart(); }
      alert(t('order.success'));
      navigate(`/orders/${createdOrderId}`);
    } catch (err: any) {
      alert(err.response?.data?.message || t('order.failed'));
    } finally {
      setLoading(false);
    }
  };

  const couponDiscount = getCouponDiscount();
  const afterCoupon = Math.max(0, subtotalBase + shippingBase - couponDiscount);
  const pointsDiscount = usePoints ? Math.min(pointsBalanceUsd, afterCoupon) : 0;
  const usedPointsInt = Math.floor(pointsDiscount * 100);
  const total = Math.max(0, afterCoupon - pointsDiscount);
  const isPayPal = form.paymentMethod === 'PAYPAL';
  const isStripe = form.paymentMethod === 'STRIPE';

  useEffect(() => {
    if (!isStripe || total <= 0 || !gbState?.groupBuyId) {
      setStripeClientSecret(null);
      setStripeIntentId(null);
      return;
    }
    if (!stripePromise) {
      stripeApi.getConfig()
        .then(r => setStripePromise(loadStripe(r.data.data.publishableKey)))
        .catch(() => setStripeError('Stripe 설정 로딩 실패'));
    }
    stripeApi.createPaymentIntent({
      groupBuyId: gbState.groupBuyId,
      quantity: gbQty,
      userCouponId: selectedCoupon?.id,
      usedPoints: usedPointsInt,
    })
      .then(r => {
        setStripeClientSecret(r.data.data.clientSecret);
        setStripeIntentId(r.data.data.paymentIntentId);
        setStripeAmountUsd(r.data.data.amountUsd);
        setStripeError(null);
      })
      .catch((err: any) => setStripeError(err.response?.data?.message || 'PaymentIntent 생성 실패'));
  }, [isStripe, total]);

  const handleStripeSuccess = async (paymentIntentId: string) => {
    if (!gbState?.groupBuyId) return;
    setLoading(true);
    try {
      const res = await stripeApi.confirm({
        stripePaymentIntentId: paymentIntentId,
        groupBuyId: gbState.groupBuyId,
        variantId: gbVariantId,
        quantity: gbQty,
        shippingAddress: buildShippingAddress(),
        userCouponId: selectedCoupon?.id,
        usedPoints: usedPointsInt,
      });
      alert(t('order.success'));
      navigate(`/orders/${res.data.data.id}`);
    } finally {
      setLoading(false);
    }
  };

  if (!cart) return null;

  return (
    <div style={styles.page}>
      <h1 style={styles.title}>{t('order.title')}</h1>
      <form onSubmit={handleOrder} style={styles.layout}>
        <div style={styles.left}>
          <section style={styles.section}>
            <h3 style={styles.sectionTitle}>{t('order.delivery')}</h3>

            <div style={styles.field}>
              <label style={styles.label}>{t('order.receiverName')}</label>
              <input type="text" placeholder={t('order.receiverName')} style={styles.input}
                required={!isPayPal && !isStripe} {...f('receiverName')} />
            </div>

            <div style={styles.field}>
              <label style={styles.label}>{t('order.receiverPhone')}</label>
              <input
                type="tel"
                placeholder="(555) 555-5555"
                style={styles.input}
                required={!isPayPal && !isStripe}
                value={form.receiverPhone}
                onChange={e => {
                  const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
                  let formatted = digits;
                  if (digits.length > 6) formatted = `(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6)}`;
                  else if (digits.length > 3) formatted = `(${digits.slice(0,3)}) ${digits.slice(3)}`;
                  else if (digits.length > 0) formatted = `(${digits}`;
                  setForm({ ...form, receiverPhone: formatted });
                }}
              />
            </div>

            <div style={styles.field}>
              <label style={styles.label}>{t('order.address')}</label>
              {MAPS_API_KEY ? (
                <AddressAutocompleteField
                  value={form.address}
                  onChange={v => setForm({ ...form, address: v })}
                  onPlaceSelected={handlePlaceSelected}
                  style={styles.input}
                  placeholder="Search US address..."
                  required={!isPayPal && !isStripe}
                />
              ) : (
                <input type="text" placeholder={t('order.address')} style={styles.input}
                  required={!isPayPal && !isStripe} {...f('address')} />
              )}
            </div>

            {(form.city || form.state || form.zipCode) && (
              <div style={styles.addressPreview}>
                {[form.city, form.state, form.zipCode].filter(Boolean).join(', ')}
              </div>
            )}

            <div style={styles.field}>
              <label style={styles.label}>{t('order.addressDetail')}</label>
              <input type="text" placeholder={t('order.addressDetail')} style={styles.input}
                required={!isPayPal && !isStripe} {...f('addressDetail')} />
            </div>

            <div style={styles.field}>
              <label style={styles.label}>{t('order.memo')}</label>
              <textarea placeholder={t('order.memoPlaceholder')} style={{ ...styles.input, height: 80, resize: 'none' }} {...f('memo')} />
            </div>
          </section>

          <section style={styles.section}>
            <h3 style={styles.sectionTitle}>{t('order.payment')}</h3>
            <PaymentMethodPicker
              total={total}
              value={form.paymentMethod}
              onChange={(v: string) => setForm({ ...form, paymentMethod: v })}
              stripeEnabled={STRIPE_ENABLED && !isCart && !!process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY}
            />
          </section>

          <section style={styles.section}>
            <h3 style={styles.sectionTitle}>Coupon</h3>
            {myCoupons.length === 0 ? (
              <p style={{ fontSize: 13, color: '#bbb' }}>No coupons available</p>
            ) : (
              <div>
                <button
                  type="button"
                  style={styles.couponToggleBtn}
                  onClick={() => setCouponOpen(o => !o)}
                >
                  {selectedCoupon
                    ? `✅ ${selectedCoupon.name} applied`
                    : `${myCoupons.length} coupon${myCoupons.length > 1 ? 's' : ''} available`}
                  <span style={{ marginLeft: 8 }}>{couponOpen ? '▲' : '▼'}</span>
                </button>
                {couponOpen && (
                  <div style={styles.couponList}>
                    <div
                      style={{ ...styles.couponOption, ...(selectedCoupon === null ? styles.couponOptionSelected : {}) }}
                      onClick={() => { setSelectedCoupon(null); setCouponOpen(false); }}
                    >
                      <span>Do not use a coupon</span>
                    </div>
                    {myCoupons.map(c => {
                      const discountLabel = c.discountType === 'FIXED' ? `$${c.discountValue} OFF` : `${c.discountValue}% OFF`;
                      const usable = !c.minOrderAmount || (subtotalBase + shippingBase) >= c.minOrderAmount;
                      return (
                        <div
                          key={c.id}
                          style={{ ...styles.couponOption, ...(selectedCoupon?.id === c.id ? styles.couponOptionSelected : {}), opacity: usable ? 1 : 0.4, cursor: usable ? 'pointer' : 'not-allowed' }}
                          onClick={() => { if (usable) { setSelectedCoupon(c); setCouponOpen(false); } }}
                        >
                          <div>
                            <span style={{ fontWeight: 700, color: '#b39ddb' }}>{discountLabel}</span>
                            <span style={{ marginLeft: 8, fontSize: 13, color: '#555' }}>{c.name}</span>
                            {c.minOrderAmount && <span style={{ fontSize: 11, color: '#aaa', marginLeft: 8 }}>Min. ${c.minOrderAmount}</span>}
                          </div>
                          {c.expiresAt && <span style={{ fontSize: 11, color: '#aaa' }}>~ {c.expiresAt.slice(0, 10)}</span>}
                        </div>
                      );
                    })}
                  </div>
                )}
                {selectedCoupon && couponDiscount > 0 && (
                  <p style={{ fontSize: 13, color: '#4caf50', marginTop: 8 }}>
                    ✅ Discount applied: -{formatPrice(couponDiscount, 'USD')}
                  </p>
                )}
              </div>
            )}
          </section>

          {pointsBalance > 0 && (
            <section style={styles.section}>
              <h3 style={styles.sectionTitle}>Points</h3>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={usePoints}
                  onChange={e => setUsePoints(e.target.checked)}
                />
                <span style={{ fontSize: 14 }}>
                  Use my points (<strong>${pointsBalanceUsd.toFixed(2)}</strong> available)
                </span>
              </label>
              {usePoints && pointsDiscount > 0 && (
                <p style={{ fontSize: 13, color: '#4caf50', marginTop: 8 }}>
                  ✅ Points applied: -{formatPrice(pointsDiscount, 'USD')}
                </p>
              )}
            </section>
          )}
        </div>

        <div style={styles.right}>
          <h3 style={styles.sectionTitle}>Order Summary</h3>
          {isCart
            ? activeItems.map(it => (
                <div key={`${it.kind}:${it.productId ?? it.groupBuyId}:${it.variantId ?? 0}`} style={styles.orderItem}>
                  <img src={toImageUrl(it.thumbnailUrl ?? '')} alt="" style={styles.orderItemImg} />
                  <div style={{ flex: 1 }}>
                    <p style={styles.orderItemName}>{it.kind === 'GROUP_BUY' ? '[Group buy] ' : ''}{it.name}{it.variantName ? ` (${it.variantName})` : ''}</p>
                    <p style={styles.orderItemQty}>{it.quantity} × ${Number(it.unitPrice).toFixed(2)}</p>
                  </div>
                  <p style={styles.orderItemTotal}>${Number(it.lineTotal).toFixed(2)}</p>
                </div>
              ))
            : gbState?.groupBuy && (
                <div style={styles.orderItem}>
                  <img src={toImageUrl(gbState.groupBuy.thumbnailUrl ?? '')} alt="" style={styles.orderItemImg} />
                  <div style={{ flex: 1 }}>
                    <p style={styles.orderItemName}>{gbState.groupBuy.title}{gbVariantName ? ` (${gbVariantName})` : ''}</p>
                    <p style={styles.orderItemQty}>{gbQty} × ${Number(gbPrice).toFixed(2)}</p>
                  </div>
                  <p style={styles.orderItemTotal}>${gbSubtotal.toFixed(2)}</p>
                </div>
              )}

          {showCombineChoice && (
            <div style={styles.combineBox}>
              <label style={styles.combineRow}>
                <input type="checkbox" checked={combineShipping} onChange={e => setCombineShipping(e.target.checked)} />
                <span style={{ fontWeight: 700 }}>Combine shipping</span>
              </label>
              <p style={styles.combineNote}>
                {combineShipping
                  ? '⚠️ Everything ships together once the latest item (group buy) is ready.'
                  : 'Products and group-buy items ship separately (shipping charged for each).'}
              </p>
              <p style={styles.combineCompare}>
                Combined ${Number(quoted?.combinedShippingFee ?? 0).toFixed(2)} · Separate ${Number(quoted?.separateShippingFee ?? 0).toFixed(2)}
              </p>
            </div>
          )}

          <div style={styles.summaryDivider} />
          <div style={styles.summaryRow}><span>Subtotal</span><span>${subtotalBase.toFixed(2)}</span></div>
          <div style={styles.summaryRow}>
            <span>Shipping{showCombineChoice ? (combineShipping ? ' (combined)' : ' (separate)') : ''}</span>
            <span>${shippingBase.toFixed(2)}</span>
          </div>
          {couponDiscount > 0 && (
            <div style={{ ...styles.summaryRow, color: '#e53935' }}>
              <span>쿠폰 할인</span>
              <span>−{formatPrice(couponDiscount, 'USD')}</span>
            </div>
          )}
          {pointsDiscount > 0 && (
            <div style={{ ...styles.summaryRow, color: '#e53935' }}>
              <span>Points</span>
              <span>−{formatPrice(pointsDiscount, 'USD')}</span>
            </div>
          )}
          <div style={styles.summaryDivider} />
          <div style={{ ...styles.summaryRow, fontWeight: 700, fontSize: 18 }}>
            <span>{t('cart.total')}</span>
            <span style={{ color: '#b39ddb' }}>{formatPrice(total, 'USD')}</span>
          </div>

          {isStripe && !process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ? (
            <div style={styles.notConfigured}>
              <p>Stripe Publishable Key가 설정되지 않았습니다.</p>
              <p style={{ fontSize: 12, color: '#aaa' }}>.env 파일에 REACT_APP_STRIPE_PUBLISHABLE_KEY를 설정하세요.</p>
            </div>
          ) : isPayPal ? (
            <div style={styles.paypalWrap}>
              <div style={styles.paypalQuote}>
                <span style={styles.paypalQuoteLabel}>PayPal 결제 금액</span>
                <span style={styles.paypalQuoteUsd}>${total.toFixed(2)} USD</span>
              </div>
              <p style={styles.paypalInfo}>{t('order.paypalInfo')}</p>
              <PayPalButtons
                style={{ layout: 'vertical', color: 'blue', shape: 'rect', label: 'pay' }}
                disabled={loading}
                onClick={async (_data, actions) => {
                  if (!validateDelivery()) return actions.reject();
                }}
                createOrder={handlePayPalCreate}
                onApprove={handlePayPalApprove}
                onError={() => alert(t('order.failed'))}
              />
            </div>
          ) : isStripe ? (
            <div style={styles.stripeWrap}>
              {stripeAmountUsd > 0 && (
                <div style={styles.stripeQuote}>
                  <span style={styles.stripeQuoteLabel}>{t('order.stripeAmount')}</span>
                  <span style={styles.stripeQuoteUsd}>≈ ${stripeAmountUsd.toFixed(2)} USD</span>
                </div>
              )}
              {stripeError && <p style={styles.stripeError}>{stripeError}</p>}
              {stripePromise && stripeClientSecret && stripeIntentId ? (
                <Elements
                  key={stripeClientSecret}
                  stripe={stripePromise}
                  options={{ clientSecret: stripeClientSecret, appearance: { theme: 'stripe' } }}
                >
                  <StripePaymentForm
                    amountUsd={stripeAmountUsd}
                    disabled={loading}
                    onBeforePay={validateDelivery}
                    onPaymentSuccess={handleStripeSuccess}
                    onPaymentError={msg => alert(msg)}
                  />
                </Elements>
              ) : !stripeError && (
                <p style={styles.stripeLoading}>{t('order.stripeLoading')}</p>
              )}
            </div>
          ) : (
            <button type="submit" style={styles.payBtn} disabled={loading}>
              {loading ? t('order.processing') : `${formatPrice(total, 'USD')} ${t('order.pay')}`}
            </button>
          )}

        </div>
      </form>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  page: { maxWidth: 1100, margin: '40px auto', padding: '0 20px' },
  title: { fontSize: 24, fontWeight: 700, marginBottom: 32 },
  layout: { display: 'flex', gap: 32, alignItems: 'flex-start' },
  left: { flex: 1, display: 'flex', flexDirection: 'column', gap: 24 },
  right: { width: 340, background: '#fafafa', borderRadius: 12, padding: 24, border: '1px solid #f0f0f0', position: 'sticky', top: 80 },
  section: { background: '#fff', borderRadius: 12, padding: 24, border: '1px solid #f0f0f0' },
  sectionTitle: { fontSize: 16, fontWeight: 700, marginBottom: 20 },
  field: { display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 },
  label: { fontSize: 13, fontWeight: 500, color: '#555' },
  input: { padding: '12px 16px', border: '1px solid #e8e8e8', borderRadius: 8, fontSize: 14, outline: 'none', width: '100%', boxSizing: 'border-box' },
  addressPreview: {
    marginTop: -10, marginBottom: 16, padding: '8px 12px',
    background: '#f0f7ff', border: '1px solid #c5ddf9', borderRadius: 6,
    fontSize: 13, color: '#1a5fa8',
  },
  orderItem: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 },
  orderItemImg: { width: 56, height: 56, objectFit: 'cover', borderRadius: 6, background: '#f5f5f5' },
  orderItemName: { fontSize: 13, fontWeight: 500, marginBottom: 2 },
  orderItemQty: { fontSize: 12, color: '#888' },
  orderItemTotal: { fontSize: 14, fontWeight: 600 },
  summaryDivider: { height: 1, background: '#e8e8e8', margin: '16px 0' },
  combineBox: { marginTop: 16, padding: 12, background: '#f6f2fc', borderRadius: 8, border: '1px solid #e6dcf5' },
  combineRow: { display: 'flex', gap: 8, alignItems: 'center', fontSize: 14, color: '#444', cursor: 'pointer' },
  combineNote: { fontSize: 12, color: '#7e57c2', marginTop: 6, lineHeight: 1.5 },
  combineCompare: { fontSize: 11, color: '#999', marginTop: 4 },
  summaryRow: { display: 'flex', justifyContent: 'space-between', fontSize: 14, color: '#555', marginBottom: 10 },
  payBtn: { width: '100%', padding: '16px', background: '#b39ddb', color: '#fff', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 700, marginTop: 20, cursor: 'pointer' },
  paypalWrap: { marginTop: 20 },
  paypalInfo: { fontSize: 12, color: '#888', textAlign: 'center', marginBottom: 12 },
  paypalQuote: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
    padding: '14px 16px', background: '#f5f9ff', border: '1px solid #d6e4f5',
    borderRadius: 8, marginBottom: 12,
  },
  paypalQuoteLabel: { fontSize: 11, color: '#5a7ab0', fontWeight: 600 },
  paypalQuoteUsd: { fontSize: 20, fontWeight: 700, color: '#003087' },
  paypalQuoteRate: { fontSize: 10, color: '#888' },
  stripeWrap: { marginTop: 20 },
  stripeQuote: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
    padding: '14px 16px', background: '#f5f3ff', border: '1px solid #ddd5fc',
    borderRadius: 8, marginBottom: 16,
  },
  stripeQuoteLabel: { fontSize: 11, color: '#635bff', fontWeight: 600 },
  stripeQuoteUsd: { fontSize: 20, fontWeight: 700, color: '#3c2fb3' },
  stripeError: { fontSize: 13, color: '#e53935', textAlign: 'center', marginBottom: 12 },
  stripeLoading: { fontSize: 12, color: '#888', textAlign: 'center', padding: 12 },
  notConfigured: { marginTop: 20, padding: '20px', background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 8, textAlign: 'center', fontSize: 14, color: '#795548' },
  couponToggleBtn: {
    width: '100%', padding: '12px 16px', background: '#fafafa', border: '1px solid #e8e8e8',
    borderRadius: 8, fontSize: 14, cursor: 'pointer', textAlign: 'left', display: 'flex', justifyContent: 'space-between',
  },
  couponList: {
    marginTop: 8, border: '1px solid #e8e8e8', borderRadius: 8, overflow: 'hidden',
  },
  couponOption: {
    padding: '12px 16px', fontSize: 13, cursor: 'pointer', display: 'flex',
    justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f5f5f5',
    background: '#fff',
  },
  couponOptionSelected: { background: '#f8f5fd', borderLeft: '3px solid #b39ddb' },
};

export default CheckoutPage;
